import { Plus, Trash2 } from 'lucide-react';
import { Database } from 'lucide-react';
import { unitOptions, formatINR, dimUnitLabel, getRoomTotal } from './boqCalc';

/**
 * Shared BOQ room spreadsheet — identical look & behaviour for project BOQs and
 * BOQ templates. When rateReadOnly is true (templates), the RATE column shows the
 * live rate pulled from the Item & Cost Sheet and is not editable (templates store
 * no rates).
 */
export default function RoomSpreadsheet({
  room, roomIdx, roomTotal, grandTotal,
  onUpdateCell, onAddRow, onDeleteRow, onOpenPicker,
  rateReadOnly = false, readOnly = false,
}) {
  return (
    <div className="space-y-2">
      {/* Room header with totals */}
      <div className="flex items-center justify-between px-1">
        <span className="text-xs text-[#6B7280]">{room.items?.length || 0} items</span>
        <div className="flex items-center gap-4">
          <span className="text-xs text-[#6B7280]">Room Total: <strong className="text-[#2C2C2E]">Rs {formatINR(roomTotal)}</strong></span>
          <span className="text-xs font-medium text-[#3A8C8C]">Grand Total: Rs {formatINR(grandTotal)}</span>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-[#E8E6E1]" data-testid="boq-spreadsheet">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="bg-[#FAFAF8]">
              <th className="px-2 py-2 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider text-center border-b border-r border-[#E8E6E1] w-10">No.</th>
              <th className="px-2 py-2 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider text-left border-b border-r border-[#E8E6E1] min-w-[220px]">Description</th>
              <th className="px-1 py-2 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider text-center border-b border-r border-[#E8E6E1] w-14">L</th>
              <th className="px-1 py-2 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider text-center border-b border-r border-[#E8E6E1] w-14">W</th>
              <th className="px-1 py-2 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider text-center border-b border-r border-[#E8E6E1] w-14">H</th>
              <th className="px-1 py-2 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider text-center border-b border-r border-[#E8E6E1] w-12">No.</th>
              <th className="px-1 py-2 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider text-center border-b border-r border-[#E8E6E1] w-20 bg-[#F5F3EF]">QNTY</th>
              <th className="px-1 py-2 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider text-center border-b border-r border-[#E8E6E1] w-16">Unit</th>
              <th className="px-1 py-2 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider text-right border-b border-r border-[#E8E6E1] w-20">RATE</th>
              <th className="px-2 py-2 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider text-right border-b border-r border-[#E8E6E1] w-24 bg-[#F5F3EF]">AMOUNT</th>
              <th className="w-8 border-b border-[#E8E6E1]" />
            </tr>
          </thead>
          <tbody>
            {room.items?.map((row, rowIdx) => {
              const showToggle = Number(row.l) > 0 && Number(row.w) > 0 && Number(row.h) > 0;
              const unitLabel = dimUnitLabel(row._dimType);
              return (
                <tr key={row.id} className="hover:bg-[#FAFAF8]/60 transition-colors group" data-testid={`boq-row-${rowIdx}`}>
                  <td className="px-2 py-1 border-b border-r border-[#E8E6E1] text-xs text-[#6B7280] text-center align-top pt-2.5">{row.sno}</td>
                  {/* Description + Specification */}
                  <td className="px-1 py-1 border-b border-r border-[#E8E6E1]">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-0.5">
                        <input value={row.description} disabled={readOnly} onChange={e => onUpdateCell(roomIdx, rowIdx, 'description', e.target.value)}
                          placeholder="Item description"
                          className="flex-1 px-1.5 py-1 text-xs font-medium bg-transparent border border-transparent hover:border-[#D4D0C8] focus:border-[#3A8C8C] rounded outline-none transition-colors"
                          data-testid={`boq-cell-desc-${rowIdx}`} />
                        {!readOnly && (
                          <button onClick={() => onOpenPicker(rowIdx)}
                            className="p-0.5 rounded hover:bg-[#3A8C8C]/10 transition-colors opacity-40 hover:opacity-100 shrink-0"
                            title="Pick from Item & Cost Sheet" data-testid={`boq-picker-btn-${rowIdx}`}>
                            <Database size={11} strokeWidth={1.5} className="text-[#3A8C8C]" />
                          </button>
                        )}
                      </div>
                      <input value={row.specification || ''} disabled={readOnly} onChange={e => onUpdateCell(roomIdx, rowIdx, 'specification', e.target.value)}
                        placeholder="Specification..."
                        className="w-full px-1.5 py-0.5 text-[10px] text-[#6B7280] bg-transparent border border-transparent hover:border-[#D4D0C8] focus:border-[#3A8C8C] rounded outline-none transition-colors"
                        data-testid={`boq-cell-spec-${rowIdx}`} />
                    </div>
                  </td>
                  {['l', 'w', 'h'].map(key => (
                    <td key={key} className="px-0.5 py-1 border-b border-r border-[#E8E6E1] align-top pt-2">
                      <input type="number" value={row[key] || ''} disabled={readOnly} onChange={e => onUpdateCell(roomIdx, rowIdx, key, e.target.value)}
                        className="w-full px-1 py-1 text-xs bg-transparent border border-transparent hover:border-[#D4D0C8] focus:border-[#3A8C8C] rounded outline-none transition-colors text-center"
                        data-testid={`boq-cell-${key}-${rowIdx}`} />
                    </td>
                  ))}
                  <td className="px-0.5 py-1 border-b border-r border-[#E8E6E1] align-top pt-2">
                    <input type="number" value={row.nos || ''} disabled={readOnly} onChange={e => onUpdateCell(roomIdx, rowIdx, 'nos', e.target.value)}
                      className="w-full px-1 py-1 text-xs bg-transparent border border-transparent hover:border-[#D4D0C8] focus:border-[#3A8C8C] rounded outline-none transition-colors text-center"
                      data-testid={`boq-cell-nos-${rowIdx}`} />
                  </td>
                  {/* QNTY (computed) + basis toggle + unit label */}
                  <td className="px-2 py-1 border-b border-r border-[#E8E6E1] text-xs font-medium text-[#2C2C2E] text-center align-top pt-2 bg-[#FAFAF8]/50"
                      data-testid={`boq-cell-qty-${rowIdx}`}>
                    <div className="flex flex-col items-center gap-0.5">
                      <span>{row.qty}{unitLabel && <span className="ml-0.5 text-[8px] text-[#6B7280] font-normal">{unitLabel}</span>}</span>
                      {showToggle && !readOnly && (
                        <div className="flex items-center gap-0.5 mt-0.5 pt-0.5 border-t border-[#E8E6E1]/70" data-testid={`boq-basis-toggle-${rowIdx}`}>
                          <button type="button" onClick={() => onUpdateCell(roomIdx, rowIdx, 'area_basis', 'lw')}
                            className={`px-1 py-0 rounded text-[8px] font-medium transition-colors ${row._basis === 'lw' ? 'bg-[#3A8C8C] text-white' : 'bg-[#E8E6E1] text-[#6B7280] hover:bg-[#D4D0C8]'}`}
                            title="Use L × W (floor / ceiling area · sq ft)" data-testid={`boq-basis-lw-${rowIdx}`}>L×W</button>
                          <button type="button" onClick={() => onUpdateCell(roomIdx, rowIdx, 'area_basis', 'lh')}
                            className={`px-1 py-0 rounded text-[8px] font-medium transition-colors ${row._basis === 'lh' ? 'bg-[#3A8C8C] text-white' : 'bg-[#E8E6E1] text-[#6B7280] hover:bg-[#D4D0C8]'}`}
                            title="Use L × H (wall / vertical area · sq ft)" data-testid={`boq-basis-lh-${rowIdx}`}>L×H</button>
                          <button type="button" onClick={() => onUpdateCell(roomIdx, rowIdx, 'area_basis', 'lwh')}
                            className={`px-1 py-0 rounded text-[8px] font-medium transition-colors ${row._basis === 'lwh' ? 'bg-[#3A8C8C] text-white' : 'bg-[#E8E6E1] text-[#6B7280] hover:bg-[#D4D0C8]'}`}
                            title="Use L × W × H (volume · cu ft)" data-testid={`boq-basis-lwh-${rowIdx}`}>L×W×H</button>
                        </div>
                      )}
                    </div>
                  </td>
                  {/* Unit */}
                  <td className="px-0.5 py-1 border-b border-r border-[#E8E6E1] align-top pt-1.5">
                    <select value={row.unit} disabled={readOnly} onChange={e => onUpdateCell(roomIdx, rowIdx, 'unit', e.target.value)}
                      className="w-full px-0.5 py-1 text-[10px] bg-transparent border border-transparent hover:border-[#D4D0C8] focus:border-[#3A8C8C] rounded outline-none text-center"
                      data-testid={`boq-cell-unit-${rowIdx}`}>
                      {unitOptions.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </td>
                  {/* RATE */}
                  <td className="px-0.5 py-1 border-b border-r border-[#E8E6E1] align-top pt-2">
                    {rateReadOnly ? (
                      <div className="text-xs text-right px-1 py-1 text-[#2C2C2E]" data-testid={`boq-cell-rate-${rowIdx}`} title="Live rate from Item & Cost Sheet">
                        {row.rate ? formatINR(row.rate) : <span className="text-[#D4D0C8]">—</span>}
                      </div>
                    ) : (
                      <input type="number" value={row.rate || ''} onChange={e => onUpdateCell(roomIdx, rowIdx, 'rate', e.target.value)}
                        className="w-full px-1 py-1 text-xs bg-transparent border border-transparent hover:border-[#D4D0C8] focus:border-[#3A8C8C] rounded outline-none transition-colors text-right"
                        data-testid={`boq-cell-rate-${rowIdx}`} />
                    )}
                  </td>
                  {/* AMOUNT */}
                  <td className="px-2 py-1 border-b border-r border-[#E8E6E1] text-xs font-semibold text-[#2C2C2E] text-right align-top pt-2.5 bg-[#FAFAF8]/50"
                      data-testid={`boq-cell-amount-${rowIdx}`}>
                    {formatINR(row.amount)}
                  </td>
                  <td className="px-1 py-1 border-b border-[#E8E6E1] align-top pt-2">
                    {!readOnly && (
                      <button onClick={() => onDeleteRow(roomIdx, rowIdx)}
                        className="opacity-0 group-hover:opacity-100 p-0.5 text-[#C4727F] hover:bg-[#C4727F]/10 rounded transition-all"
                        data-testid={`boq-delete-row-${rowIdx}`}>
                        <Trash2 size={11} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-[#F5F3EF]">
              <td colSpan={9} className="px-2 py-2.5 text-xs font-bold text-[#2C2C2E] text-right border-t border-[#D4D0C8] uppercase tracking-wider">Room Total</td>
              <td className="px-2 py-2.5 text-sm font-bold text-[#3A8C8C] text-right border-t border-[#D4D0C8]" data-testid="boq-room-total">Rs {formatINR(roomTotal)}</td>
              <td className="border-t border-[#D4D0C8]" />
            </tr>
          </tfoot>
        </table>
      </div>

      <button onClick={() => onAddRow(roomIdx)} data-testid="boq-add-row"
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-[#3A8C8C] hover:bg-[#3A8C8C]/5 transition-colors ${readOnly ? 'hidden' : ''}`}>
        <Plus size={14} strokeWidth={1.5} /> Add Row
      </button>
    </div>
  );
}

export function GrandTotalView({ rooms, grandTotal }) {
  return (
    <div className="space-y-4" data-testid="boq-grand-total-view">
      <div className="glass-card rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h4 className="section-heading text-sm font-semibold font-['Manrope']">Estimate Summary</h4>
          <div className="text-right">
            <p className="text-[10px] text-[#6B7280] uppercase tracking-wider">Grand Total</p>
            <p className="text-2xl font-bold text-[#3A8C8C] font-['Manrope']" data-testid="boq-grand-total-amount">Rs {formatINR(grandTotal)}</p>
          </div>
        </div>
        <div className="overflow-hidden rounded-lg border border-[#E8E6E1]">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#FAFAF8]">
                <th className="px-4 py-2 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider text-left border-b border-[#E8E6E1]">Room / Area</th>
                <th className="px-4 py-2 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider text-center border-b border-[#E8E6E1]">Items</th>
                <th className="px-4 py-2 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider text-right border-b border-[#E8E6E1]">Amount (Rs)</th>
                <th className="px-4 py-2 text-[10px] font-semibold text-[#6B7280] uppercase tracking-wider text-right border-b border-[#E8E6E1]">% Share</th>
              </tr>
            </thead>
            <tbody>
              {rooms.map((room, idx) => {
                const roomTotal = getRoomTotal(room);
                const pct = grandTotal > 0 ? ((roomTotal / grandTotal) * 100).toFixed(1) : '0.0';
                return (
                  <tr key={room.id} className="hover:bg-[#FAFAF8]/50 transition-colors" data-testid={`boq-gt-room-${idx}`}>
                    <td className="px-4 py-2.5 border-b border-[#E8E6E1]"><span className="text-sm font-medium text-[#2C2C2E]">{room.name}</span></td>
                    <td className="px-4 py-2.5 border-b border-[#E8E6E1] text-center"><span className="text-xs text-[#6B7280]">{room.items?.length || 0}</span></td>
                    <td className="px-4 py-2.5 border-b border-[#E8E6E1] text-right"><span className="text-sm font-semibold text-[#2C2C2E]">{formatINR(roomTotal)}</span></td>
                    <td className="px-4 py-2.5 border-b border-[#E8E6E1] text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-1.5 bg-[#E8E6E1] rounded-full overflow-hidden">
                          <div className="h-full bg-[#3A8C8C] rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-[#6B7280] w-10 text-right">{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-[#F5F3EF]">
                <td className="px-4 py-3 font-bold text-sm text-[#2C2C2E] border-t border-[#D4D0C8]">Grand Total</td>
                <td className="px-4 py-3 text-center border-t border-[#D4D0C8]"><span className="text-xs font-medium text-[#6B7280]">{rooms.reduce((s, r) => s + (r.items?.length || 0), 0)}</span></td>
                <td className="px-4 py-3 text-right border-t border-[#D4D0C8]"><span className="text-base font-bold text-[#3A8C8C]">Rs {formatINR(grandTotal)}</span></td>
                <td className="px-4 py-3 text-right border-t border-[#D4D0C8]"><span className="text-xs font-medium text-[#6B7280]">100%</span></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
